import React, {
  createContext,
  useContext,
  useState,
  PropsWithChildren,
} from 'react'
import {
  OtherItem,
  OtherTrayItemsContextType,
  OtherTrayItemsProviderProps,
} from './OtherTrayItems.types'

export const OtherTrayItemsContext =
  createContext<OtherTrayItemsContextType | null>(null)

export const useOtherTrayItemsContext = () => {
  const context = useContext(OtherTrayItemsContext)

  if (!context) {
    throw new Error(
      'useOtherTrayItemsContext must be used within a OtherTrayItemsProvider'
    )
  }

  return context
}

export const useOtherTrayItems = (availableItems: OtherItem[]) => {
  const context = useContext(OtherTrayItemsContext)

  if (!context) {
    throw new Error(
      'useOtherTrayItems must be used within a OtherTrayItemsProvider'
    )
  }

  const addItem = (deviceId: string, count: number) => {
    const itemToAdd = availableItems.find((item) => item.device_id === deviceId)
    if (itemToAdd) {
      context.addItem({
        ...itemToAdd,
        count,
      })
    }
  }

  return {
    ...context,
    addItem,
  }
}

export const OtherTrayItemsProvider: React.FC<
  PropsWithChildren<OtherTrayItemsProviderProps>
> = ({ children, items = [], setItems }) => {
  const [modalOpen, setModalOpen] = useState(false)

  const addItem = (item: OtherItem) => {
    const existingItem = items.find((i) => i.device_id === item.device_id)
    if (!existingItem) {
      setItems((prevItems) => [...prevItems, item])
    }
  }

  const removeItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.device_id !== id))
  }

  const setCount = (id: string, count: number) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.device_id === id ? { ...item, count } : item
      )
    )
  }

  return (
    <OtherTrayItemsContext.Provider
      value={{
        modalOpen,
        setModalOpen,
        selectedItems: items,
        addItem,
        removeItem,
        setCount,
      }}
    >
      {children}
    </OtherTrayItemsContext.Provider>
  )
}
